import { Football, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";
import PublicNav from "@/components/PublicNav";

const TOOLS = [
  {
    name: "KeepTradeCut",
    method: "Crowdsourced ranking game (Keep/Trade/Cut votes)",
    strength: "Values update in near real-time as public sentiment shifts — breakouts and injuries move value within hours.",
    tradeoff: "Reflects public perception, which isn't always what players are actually being traded for in real leagues.",
  },
  {
    name: "FantasyCalc",
    method: "Real trades from Sleeper, MFL, and Fleaflicker leagues",
    strength: "Market price based on what managers are actually giving up, not a sentiment survey. Deeper trade-context analysis.",
    tradeoff: "Values can lag slightly behind breaking news since they're derived from completed trades, not predictions.",
  },
  {
    name: "Dynasty Trade Maker",
    method: "FantasyCalc real-trade data (150,000+ Sleeper leagues) + direct Sleeper integration",
    strength: "Instant FAIR/CLOSE/LOPSIDED verdicts, 6 personalized trade packages from your actual roster, Draft War Room — all in one place.",
    tradeoff: "Currently Sleeper-only (no MFL or Fleaflicker league import yet).",
  },
];

export default function TradeValueToolsComparison() {
  const hideNav = useIsAppNavVisible();
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Best Dynasty Trade Value Tools Compared (2026): KeepTradeCut vs. FantasyCalc vs. Dynasty Trade Maker" description="An honest comparison of the top dynasty trade value tools — how KeepTradeCut, FantasyCalc, and Dynasty Trade Maker calculate value, and which fits your league." path="/compare/dynasty-trade-value-tools" />
      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-10">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">COMPARISON</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-6" data-testid="tools-comparison-title">DYNASTY TRADE VALUE TOOLS <span className="text-[#ccff00]">COMPARED</span></h1>

        <div className="space-y-8 text-sm text-[#a3a3a3] leading-relaxed">
          <p>There are a few well-known dynasty trade value tools, and they don't all calculate value the same way — which matters, because a "fair" trade on one tool can look lopsided on another. Here's how the major ones actually work.</p>

          {/* Tool Cards */}
          <div className="space-y-4">
            {TOOLS.map(tool => (
              <div key={tool.name} className="border border-[#262626] bg-[#171717] p-5">
                <h2 className="font-heading text-xl text-white uppercase mb-3">{tool.name}</h2>
                <div className="space-y-2">
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#555]">METHOD</span>
                    <p className="text-sm text-[#a3a3a3]">{tool.method}</p>
                  </div>
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#4ade80]">STRENGTH</span>
                    <p className="text-sm text-[#a3a3a3]">{tool.strength}</p>
                  </div>
                  <div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#888]">TRADEOFF</span>
                    <p className="text-sm text-[#888]">{tool.tradeoff}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHICH ONE SHOULD YOU USE?</h2>
            <p className="mb-3">If you want a pure sentiment gauge on "who does the fantasy community currently rate highest," KeepTradeCut's crowdsourced model is built for exactly that. If you want values grounded in what's actually being traded in real leagues, both FantasyCalc and Dynasty Trade Maker use that same real-trade foundation.</p>
            <p>The difference is that Dynasty Trade Maker layers direct Sleeper integration and instant trade verdicts on top, so you're not manually cross-referencing a value chart against your own roster every time you get an offer. For Sleeper players specifically, that integration is the practical difference: connect your league once and every trade check after that takes seconds.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">FAQ</h2>
            <div className="space-y-3">
              {[
                { q: "Why do different trade calculators give different values for the same player?", a: "Because they calculate value differently — crowdsourced ranking surveys and real-trade market data can diverge, especially right after a breakout game or injury." },
                { q: "Is a free dynasty trade calculator accurate?", a: "The free tools covered here are all built on large real datasets, which makes them reasonably reliable — but no calculator accounts for your league's exact settings the way a manual review can." },
                { q: "Do these tools work for Superflex leagues?", a: "Yes — all three offer Superflex-specific values, since QB scarcity changes value so significantly that a 1QB-only chart would be unusable for Superflex trades." },
              ].map(f => (
                <div key={f.q} className="border border-[#262626] bg-[#171717] p-4">
                  <p className="text-sm text-white font-semibold mb-1">{f.q}</p>
                  <p className="text-xs text-[#888]">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-10 border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">TRY IT YOURSELF</h3>
          <p className="text-xs text-[#888] mb-4">Use the free <Link to="/trade-calculator" className="text-[#ccff00] no-underline hover:underline">Trade Calculator</Link> to evaluate any trade instantly, or import your Sleeper league for AI-powered suggestions.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/trade-calculator" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold">FREE TRADE CALCULATOR</Link>
            <a href="/" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">Import Your League <ArrowRight size={11} /></a>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
